import {
  ActionKey,
  ACTION_KEY_GROUPS,
  SIDEBAR_DEFINITION,
  getConfigurablePages,
} from '@pokerroom/types';
import type { FeatureKey, PagePermissionDto } from '@pokerroom/types';
import { buildNavItems } from '../../components/layout/navItems';

const navigation = buildNavItems((key) => key);
const pageOrder = new Map(
  navigation
    .flatMap((item) => [item, ...(item.children ?? [])])
    .filter((item) => item.path)
    .map((item, index) => [item.path!, index]),
);
const groupOrder = new Map(navigation.map((item, index) => [item.label, index]));
const rank = (value: number | undefined) => value ?? Number.MAX_SAFE_INTEGER;

export const ROLE_PAGE_GROUPS = SIDEBAR_DEFINITION.map((group) => ({
  ...group,
  ...(group.children
    ? {
        children: group.children.map((child) =>
          child.pagePath === '/admin/points-config'
            ? { ...child, labelKey: 'nav.memberSettingsPages' }
            : child,
        ),
      }
    : {}),
}));

export function getRolePageGroups(hasFeature?: (key: FeatureKey) => boolean) {
  const paths = new Set<string>(getConfigurablePages(hasFeature).map((page) => page.pagePath));
  return ROLE_PAGE_GROUPS.flatMap((entry) => {
    if (entry.pagePath) return paths.has(entry.pagePath) ? [entry] : [];
    const children = entry.children!.filter((child) => paths.has(child.pagePath));
    return children.length ? [{ ...entry, children }] : [];
  });
}

export function initialPagePermissions(permissions: PagePermissionDto[] = []) {
  return Object.fromEntries([
    ...getConfigurablePages().map((page) => [page.pagePath, false] as const),
    ...permissions.map((page) => [page.pagePath, page.isVisible] as const),
  ]);
}

export function countPagePermissions(
  permissions: PagePermissionDto[],
  hasFeature?: (key: FeatureKey) => boolean,
) {
  const paths = new Set<string>(getConfigurablePages(hasFeature).map((page) => page.pagePath));
  return {
    visible: new Set(
      permissions
        .filter((page) => page.isVisible && paths.has(page.pagePath))
        .map((page) => page.pagePath),
    ).size,
    total: paths.size,
  };
}

const actionNavigation: Record<string, string> = {
  actionGroup_dashboard: 'nav.dashboard',
  actionGroup_venue: 'nav.tables',
  actionGroup_member: 'nav.members',
  actionGroup_memberSettings: 'nav.memberSettings',
  actionGroup_accounting: 'nav.accounting',
  actionGroup_tournament: 'nav.tournament',
  actionGroup_blindStructure: 'nav.tournament',
  actionGroup_template: 'nav.tournament',
  actionGroup_timer: 'nav.tournament',
  actionGroup_player: 'nav.tournament',
  actionGroup_display: 'nav.tvDisplay',
  actionGroup_user: 'nav.system',
  actionGroup_role: 'nav.system',
  actionGroup_branding: 'nav.system',
  actionGroup_i18n: 'nav.system',
  actionGroup_system: 'nav.system',
};

const memberSettingActions = [ActionKey.MEMBER_WALLET_MANAGE];
const memberConfigurationActions = [
  ActionKey.MEMBER_LEVEL_MANAGE,
  ActionKey.MEMBER_ASSET_TYPE_MANAGE,
  ActionKey.MEMBER_PORTAL_MANAGE,
];

export const ROLE_ACTION_GROUPS = ACTION_KEY_GROUPS.flatMap((group) =>
  group.labelKey === 'actionGroup_member'
    ? [
        {
          ...group,
          actions: [
            ...group.actions.filter(
              (action) =>
                !memberSettingActions.includes(action) &&
                !memberConfigurationActions.includes(action),
            ),
            ...memberConfigurationActions,
          ],
        },
        { labelKey: 'actionGroup_memberSettings', actions: memberSettingActions },
      ]
    : [group],
).sort(
  (a, b) =>
    rank(groupOrder.get(actionNavigation[a.labelKey] ?? '')) -
    rank(groupOrder.get(actionNavigation[b.labelKey] ?? '')),
);

export function orderPagePermissions<T extends { pagePath: string }>(permissions: T[]): T[] {
  return [...permissions].sort(
    (a, b) => rank(pageOrder.get(a.pagePath)) - rank(pageOrder.get(b.pagePath)),
  );
}
